import dynamic from 'next/dynamic';
import { sanitizeHTML } from "@/lib/utils";
const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const InstagramFeedClient = dynamic(() => import('./InstagramFeedClient'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface DirectusInterfaceData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  widget_id?: string;
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // button
  "button.label",
  "button.href",

  // widget id
  "widget_id",
] as const;

interface InstagramFeedProps {
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
  data?: DirectusInterfaceData;
}

export default function InstagramFeed({
  zIndex,
  thresholds,
  transitionTime,
  mobileThresholds,
  helperRotation,
  data
}: InstagramFeedProps) {
  const widgetId = data?.widget_id || "";

  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, opacity: 1 },
              above: { y: "100vh", opacity: 1 },
              below: { y: "-100vh", opacity: 1 },
            },
            mobile: {
              visible: { y: 0, opacity: 1 },
              above: { y: "100vh", opacity: 1 },
              below: { y: 0, opacity: 0 },
            },
          }}
        >
          <div className="h-full overflow-hidden relative w-full flex items-start lg:items-center bg-white">
            <div className="gl-container mx-auto max-w-screen-3xl">
              {/* Header Content */}
              <div className="lg:px-0 mb-12 lg:text-center">
                <div>
                  <AnimatedSection
                    section="h2"
                    className="text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-6"
                    mobileThresholds={[
                      mobileThresholds?.[0]?.[0],
                      mobileThresholds?.[0]?.[mobileThresholds.length - 1]?.[1],
                    ]}
                    variants={{
                      desktop: {
                        visible: { opacity: 1, y: 0 },
                        above: { opacity: 1, y: 0 },
                        below: { opacity: 1, y: 0 },
                      },
                      mobile: {
                        visible: { opacity: 1, y: 0 },
                        above: { opacity: 1, y: "50vh" },
                        below: { opacity: 1, y: 0 },
                      },
                    }}
                  >
                    {data?.heading?.text}
                  </AnimatedSection>
                </div>

                <AnimatedSection
                    section="div"
                    
                    mobileThresholds={[
                      mobileThresholds?.[0]?.[0],
                      mobileThresholds?.[0]?.[mobileThresholds.length - 1]?.[1],
                    ]}
                    variants={{
                      desktop: {
                        visible: { opacity: 1, y: 0 },
                        above: { opacity: 1, y: 0 },
                        below: { opacity: 1, y: 0 },
                      },
                      mobile: {
                        visible: { opacity: 1, y: 0 },
                        above: { opacity: 1, y: "100vh" },
                        below: { opacity: 1, y: 0 },
                      },
                    }}
                  >
                  <h2 
                    className="text-[2rem] font-essonnes font-light leading-normal md:text-5xl lg:text-[3.375rem] text-primary tracking-tight lg:mx-auto"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.title || data?.heading?.text || "")}
                  />
                   <p 
                    className="text-[1rem] md:text-[1.75rem] font-normal md:font-greycliff text-primary leading-normal md:leading-snug tracking-tight xl:mx-auto [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                   />
                </AnimatedSection>
              </div>

              {/* Instagram Feed Client Component */}
              <InstagramFeedClient widgetId={widgetId} />

              {/* CTA Button */}
              <div className="px-7 lg:px-0 lg:flex lg:justify-end mt-8">
                <a 
                  href={data?.button?.href || "#"} 
                  className="w-full lg:w-auto px-12 py-2.5 bg-primary rounded-3xl text-white text-xl font-medium font-greycliff leading-7 tracking-tight hover:bg-secondary transition-colors inline-block text-center"
                >
                  {data?.button?.label}
                </a>
              </div>
            </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full pt-16 2xl:pt-32">
          <div className="h-full overflow-hidden relative w-full flex items-start lg:items-center bg-white">
            <div className="gl-container mx-auto max-w-screen-3xl">
              {/* Header Content */}
              <div className="xl:px-0 mb-12 xl:text-center">
                <div>
                  <h2 className="text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-6">
                    {data?.heading?.text}
                  </h2>
                </div>

                <div>
                  <h2 
                    className="text-[2rem] font-essonnes font-light leading-normal md:text-5xl lg:text-[3.375rem] text-primary tracking-tight xl:mx-auto"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                  />
                </div>
              </div>

              {/* Instagram Feed Client Component */}
              <InstagramFeedClient widgetId={widgetId} />

              {/* CTA Button */}
              <div className="px-7 xl:px-0 xl:flex xl:justify-end mt-8">
                <a 
                  href={data?.button?.href || "#"} 
                  className="w-full xl:w-auto px-12 py-2.5 bg-primary rounded-3xl text-white text-xl font-medium font-greycliff leading-7 tracking-tight hover:bg-secondary transition-colors inline-block text-center"
                >
                  {data?.button?.label}
                </a>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
